import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Currency } from '$api-client/types.gen'
import { CreateServiceContent } from './content'
import { Form } from '$components'
import { ServiceColour } from '$features/services/components/colour-input'
import { CreateServiceControls } from './controls'
import { pricingVariantSchema } from '../shared/pricing-variant-schema'

export enum Input {
  Name = 'name',
  Description = 'description',
  Colour = 'colour',
  Variants = 'variants',
  Currency = 'currency',
  VariantPrice = 'price',
  VariantDuration = 'duration',
  Notes = 'notes',
}

export const createServiceSchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
  [Input.Description]: z.string()
    .max(1024, { message: 'Description must be less than 1024 characters.' })
    .optional(),
  [Input.Colour]: z.string().nullable().refine((val) => val !== null, {
    message: 'Colour is required.',
  }),
  [Input.Notes]: z.string()
    .max(2048, { message: 'Internal notes must be less than 2048 characters.' })
    .optional(),
  [Input.Currency]: z.object({
    id: z.nativeEnum(Currency),
    value: z.nativeEnum(Currency),
  }),
  [Input.Variants]: z.array(pricingVariantSchema).min(1, { message: 'At least one pricing variant must be configured.' }),
}).refine(
  (values) => values[Input.Variants].some((variant) => variant.is_default),
  {
    path: [Input.Variants],
    message: 'A default price is required.',
  },
)

export type CreateServiceSchema = z.infer<typeof createServiceSchema>

type CreateServiceFormContextType = {
  onSubmit: (values: CreateServiceSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const CreateServiceFormContext = createContext<CreateServiceFormContextType | undefined>(undefined)

type CreateServiceFormProps = CreateServiceFormContextType & {
  children?: ReactNode,
}

export function CreateServiceForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
}: CreateServiceFormProps) {

  const context: CreateServiceFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <CreateServiceFormContext.Provider value={context}>
      <Form<CreateServiceSchema, typeof createServiceSchema>
        schema={createServiceSchema}
        onSubmit={onSubmit}
        defaultValues={{
          colour: ServiceColour.Gray,
          variants: [
            {
              is_default: true,
              price: 30,
              duration: 30,
            },
          ],
          currency: { id: Currency.GBP, value: Currency.GBP },
        } as CreateServiceSchema}
      >
        {children}
      </Form>
    </CreateServiceFormContext.Provider>
  )
}

CreateServiceForm.Content = CreateServiceContent
CreateServiceForm.Controls = CreateServiceControls

export function useCreateServiceForm() {
  const context = useContext(CreateServiceFormContext)

  if (context === undefined) {
    throw new Error('useCreateServiceForm must be used within a CreateServiceFormProvider')
  }

  return context
}
